import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ACCOUNT_COLOR_PALETTE } from '@/features/accounts/account-colors';
import { formatAccountLabel, visibleAccounts } from '@/features/accounts/accounts.api';
import { useAccountColors } from '@/features/accounts/useAccountColors';
import { useAccounts } from '@/features/accounts/useAccounts';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/store/ui';

/** Label for the aggregate (all-accounts) scope — matches the sidebar's
 *  pinned row copy. */
const ALL_ACCOUNTS_LABEL = '所有账号';

/**
 * Compact account-scope filter for the conversation-column header.
 *
 * A Radix `DropdownMenu` (same vendored primitives as `AccountSidebar`'s
 * agent-identity menu) whose trigger names the current scope — 「所有账号」
 * when `selectedAccountId === null`, else the selected account's identity dot
 * + `formatAccountLabel`. The menu lists 「所有账号」 first, then every account
 * (each with its color dot); picking one drives `setSelectedAccountId`.
 *
 * This is the point of the control: it duplicates the account-rail's selection
 * so an agent can re-scope the inbox even when the rail is collapsed to zero
 * width (the header is always visible). It reuses the already-cache-warm
 * `useAccounts()` query, so it never issues a fetch of its own.
 *
 * Loading / error tolerance: the trigger always renders (aggregate label, or
 * the selected account's label once the list resolves — it can't name an
 * account it doesn't have yet, so it falls back to 「所有账号」 rather than
 * crash); the menu shows a quiet disabled placeholder line instead of an empty
 * list.
 */
export function AccountFilterMenu(): React.JSX.Element {
  const selectedAccountId = useUIStore((s) => s.selectedAccountId);
  const setSelectedAccountId = useUIStore((s) => s.setSelectedAccountId);
  const accountsQuery = useAccounts();
  const accountColors = useAccountColors();

  const allAccounts = accountsQuery.data ?? [];
  // The menu offers the POOL only (hidden / archived accounts are out of it);
  // the trigger still names a selected account from the full list, so a
  // scope that was just hidden in another console never reads as 「所有账号」
  // while the inbox is still filtered to it.
  const accounts = visibleAccounts(allAccounts);
  const selectedAccount =
    selectedAccountId !== null
      ? allAccounts.find((account) => account.id === selectedAccountId)
      : undefined;

  const triggerColor = selectedAccount
    ? accountColors.get(selectedAccount.id)
    : undefined;
  const triggerLabel = selectedAccount
    ? formatAccountLabel(selectedAccount)
    : ALL_ACCOUNTS_LABEL;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          aria-label="按账号过滤"
          // Match the rail-toggle button's neumorphic chrome (raised → inset on
          // press); a pill rather than an icon square so it can hold the label.
          // `shrink-0` + the truncating label keep the header row from wrapping
          // at the panel's min width.
          className="ml-auto flex h-8 shrink-0 items-center gap-1.5 rounded-[10px] px-2.5 text-[13px] font-bold shadow-neu-raised-sm hover:shadow-neu-raised active:shadow-neu-inset-press"
        >
          {triggerColor && (
            <span
              aria-hidden="true"
              data-testid="account-filter-dot"
              className={cn('size-2 shrink-0 rounded-[3px]', triggerColor.swatch)}
            />
          )}
          {/* Label cap lives in index.css (`.conv-filter-label`: 6rem, and a
              tighter 2.5rem below the 15.625rem container tier) so the
              header's never-clip width budget holds at every panel width. */}
          <span className="conv-filter-label truncate">{triggerLabel}</span>
          <ChevronDown
            className="size-3.5 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-[60vh] min-w-[11rem] overflow-y-auto"
      >
        <DropdownMenuItem onSelect={() => setSelectedAccountId(null)}>
          {ALL_ACCOUNTS_LABEL}
        </DropdownMenuItem>
        {accountsQuery.isError ? (
          <DropdownMenuItem disabled>账号加载失败</DropdownMenuItem>
        ) : accounts.length === 0 ? (
          <DropdownMenuItem disabled>
            {accountsQuery.isLoading ? '加载中…' : '暂无账号'}
          </DropdownMenuItem>
        ) : (
          accounts.map((account) => {
            // Falls back to the palette's first entry rather than an unstyled
            // dot — in practice the id is always in the map (built from this
            // same list).
            const color = accountColors.get(account.id) ?? ACCOUNT_COLOR_PALETTE[0];
            return (
              <DropdownMenuItem
                key={account.id}
                onSelect={() => setSelectedAccountId(account.id)}
              >
                <span
                  aria-hidden="true"
                  className={cn('size-2 shrink-0 rounded-[3px]', color.swatch)}
                />
                <span className="truncate">{formatAccountLabel(account)}</span>
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
